import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Alert,
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useState } from 'react';

import { useGetPermissions } from '../api/getPermissions';
import { useRoleForm } from '../hooks/useRoleForm';
import { roleTypes } from '../types';

import { DepartmentsCheckboxes } from './DepartmentsCheckboxes';
import { PageAccessCheckboxes } from './PageAccessCheckboxes';
import { PermissionsCheckboxes } from './PermissionsCheckboxes';

type Props = {
  role?: roleTypes.Role;
};

export const RoleModal = NiceModal.create(({ role }: Props) => {
  const modal = useModal();
  const isEdit = !!role;
  const [activeTab, setActiveTab] = useState<string | null>('general');

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    isError: permissionsError,
    refetch: refetchPermissions,
  } = useGetPermissions();
  const permissions = permissionsData?.permissions ?? [];

  const { form, isLoading, handleSubmit, togglePermission } = useRoleForm({
    role,
    onSuccess: modal.remove,
    // Switch to the General tab so the user sees the name validation error.
    onValidationError: () => setActiveTab('general'),
  });

  const isWildcard = form.values.permissions.includes('*');

  return (
    <Modal
      centered
      size="lg"
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={modal.remove}
      title={isEdit ? 'Edit role' : 'New role'}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <Tabs value={activeTab} onChange={setActiveTab} variant="default">
            <Tabs.List mb="md">
              <Tabs.Tab value="general">General</Tabs.Tab>
              <Tabs.Tab value="permissions">Permissions</Tabs.Tab>
              <Tabs.Tab value="page-access" disabled={isWildcard}>
                Page access
              </Tabs.Tab>
              <Tabs.Tab value="managers" disabled={isWildcard}>
                Managers
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="general">
              <Stack gap="sm">
                <TextInput label="Name" placeholder="Role name" {...form.getInputProps('name')} />
                <Textarea
                  label="Description"
                  placeholder="Optional description"
                  autosize
                  minRows={2}
                  {...form.getInputProps('description')}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="permissions">
              <ScrollArea.Autosize mah="60vh" offsetScrollbars>
                <Stack gap="sm" pr="xs">
                  {permissionsLoading ? (
                    <Text size="sm" c="dimmed">
                      Loading permissions…
                    </Text>
                  ) : permissionsError ? (
                    <Alert color="red" title="Failed to load permissions">
                      <Text size="sm">Could not load the permissions catalog.</Text>
                      <Button
                        size="xs"
                        variant="outline"
                        color="red"
                        mt="xs"
                        onClick={() => refetchPermissions()}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : (
                    <PermissionsCheckboxes
                      permissions={permissions}
                      selected={form.values.permissions}
                      onToggle={togglePermission}
                    />
                  )}
                </Stack>
              </ScrollArea.Autosize>
            </Tabs.Panel>

            <Tabs.Panel value="page-access">
              <ScrollArea.Autosize mah="60vh" offsetScrollbars>
                <Stack gap="sm" pr="xs">
                  <Text size="xs" c="dimmed">
                    Pages visible to this role, grouped by department
                  </Text>
                  <PageAccessCheckboxes
                    value={form.values.pageAccess}
                    onChange={(pageAccess) => form.setFieldValue('pageAccess', pageAccess)}
                  />
                </Stack>
              </ScrollArea.Autosize>
            </Tabs.Panel>

            <Tabs.Panel value="managers">
              <Stack gap="sm">
                <Text size="xs" c="dimmed">
                  Departments whose managers are visible to this role (cross-account). Empty
                  selection means no managers are visible.
                </Text>
                <DepartmentsCheckboxes
                  value={form.values.managerDepartmentAccess}
                  onChange={(managerDepartmentAccess) =>
                    form.setFieldValue('managerDepartmentAccess', managerDepartmentAccess)
                  }
                />
              </Stack>
            </Tabs.Panel>
          </Tabs>

          <Group justify="right" mt="md">
            <Button variant="outline" onClick={modal.remove} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={permissionsLoading || !!permissionsError}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
});
